import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';

// Contextos
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';

// Estilos
import styles from './loginparticipantepage.module.css';

const DEBUG = false;
const dbg = (...args) => { if (DEBUG) console.log('[LoginParticipanteSenhaPage]', ...args); };

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function formatTelefoneMask(numeros) {
  const only = String(numeros || '').replace(/\D/g, '').slice(0, 11);
  const d1 = only.slice(0, 2);
  const d2 = only.slice(2, 7);
  const d3 = only.slice(7, 11);
  if (!only) return '';
  if (only.length <= 2) return `(${d1}`;
  if (only.length <= 7) return `(${d1}) ${d2}`;
  return `(${d1}) ${d2}-${d3}`;
}

/**
 * Decide para onde voltar após login:
 * 1) se tiver state.from.pathname -> volta pra lá
 * 2) senão, se tiver turma + dest (res|ead) -> volta /dest/turma
 * 3) senão -> /participante/page
 */
function getRedirectPath(location, turmaParam, destParam) {
  const fromPath = location?.state?.from?.pathname || '';
  if (fromPath) return fromPath;

  const turma = (turmaParam || '').trim();
  const dest = (destParam || '').trim().toLowerCase();

  if (turma && (dest === 'ead' || dest === 'res')) {
    return `/${dest}/${turma}`;
  }

  return '/participante/page';
}

export default function LoginParticipanteSenhaPage() {
  const { loginParticipante, isAuthenticated } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const query = useQuery();

  const [senha, setSenha] = useState('');
  const [loading, setLoading] = useState(false);

  const celParam = query.get('cel') || '';
  const turmaParam = query.get('turma') || '';
  const destParam = query.get('dest') || ''; // 'res' | 'ead'
  const celMascarado = formatTelefoneMask(celParam);

  const senhaRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!celParam) {
      showToast('Telefone não informado. Volte e tente novamente.', 3000, 'error');
      navigate('/participante/login', { replace: true });
      return;
    }
    if (!senha) {
      showToast('Informe sua senha.', 2500, 'warning');
      return;
    }

    setLoading(true);

    const redirectTo = getRedirectPath(location, turmaParam, destParam);
    dbg('submit -> redirectTo:', redirectTo, 'turmaParam:', turmaParam, 'destParam:', destParam);

    const result = await loginParticipante(celParam, senha);

    if (result?.success) {
      showToast('Login realizado com sucesso!', 3000, 'success');
      navigate(redirectTo, { replace: true });
    } else {
      if (result?.message === 'Participante ainda não cadastrado') {
        showToast(result.message, 3000, 'error');

        // Leva pro cadastro preservando turma/dest e state.from
        const params = new URLSearchParams();
        if (turmaParam) params.set('turma', turmaParam);
        if (destParam) params.set('dest', destParam);
        const s = params.toString();
        const cadastroUrl = `/participante/cadastro${s ? `?${s}` : ''}`;

        navigate(cadastroUrl, {
          replace: true,
          state: { from: location.state?.from || null }
        });
      } else {
        showToast(result?.message || 'Falha no login.', 3000, 'error');
      }
    }

    setLoading(false);
  };

  // Se já autenticado, volta imediatamente (mesma regra do submit)
  useEffect(() => {
    if (isAuthenticated) {
      const redirectTo = getRedirectPath(location, turmaParam, destParam);
      dbg('isAuthenticated -> redirectTo:', redirectTo);
      navigate(redirectTo, { replace: true });
    }
  }, [isAuthenticated, location, navigate, turmaParam, destParam]);

  useEffect(() => {
    senhaRef.current?.focus();
  }, []);

  // Link "voltar" preservando turma/dest e state.from
  const loginBackUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (turmaParam) params.set('turma', turmaParam);
    if (destParam) params.set('dest', destParam);
    const s = params.toString();
    return `/participante/login${s ? `?${s}` : ''}`;
  }, [turmaParam, destParam]);

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>Digite sua senha</h2>

      {DEBUG && (
        <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
          <div><strong>cel:</strong> {celParam || '(vazio)'} | <strong>turma:</strong> {turmaParam || '(vazio)'} | <strong>dest:</strong> {destParam || '(vazio)'}</div>
          <div><strong>state.from.pathname:</strong> {location.state?.from?.pathname || '(vazio)'}</div>
        </div>
      )}

      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.mutedNote} style={{ marginBottom: 8 }}>
          Entraremos com o número:
          <strong> {celMascarado || '(não informado)'}</strong>
        </div>

        <input
          type="password"
          ref={senhaRef}
          placeholder="Senha"
          value={senha}
          onChange={(e) => setSenha(e.target.value)}
          className={styles.input}
          required
        />

        <button type="submit" className={styles.button} disabled={loading}>
          {loading ? 'Entrando...' : 'Entrar'}
        </button>

        <p className={styles.linkText}>
          Digitou o número errado?{' '}
          <Link
            to={loginBackUrl}
            state={{ from: location.state?.from || null }}
            className={styles.link}
          >
            VOLTAR
          </Link>
        </p>

        <Link to="/participante/recuperar" className={styles.link}>
          Esqueceu sua senha?
        </Link>
      </form>
    </div>
  );
}